// Utility to add sample YBT data for testing Vietnamese translations
// Run this from browser console: addSampleYBTData()

export const addSampleYBTData = async () => {
  try {
    console.log('🔧 Adding sample YBT data for testing...');
    
    // Get current patient ID from URL or context
    const currentUrl = window.location.href;
    const patientIdMatch = currentUrl.match(/patients\/([a-f0-9]{24})/);
    
    if (!patientIdMatch) {
      console.error('❌ Could not find patient ID in current URL');
      return { success: false, error: 'No patient ID found' };
    }
    
    const patientId = patientIdMatch[1];
    console.log(`📋 Using patient ID: ${patientId}`);
    
    // Sample YBT data that will trigger calculations
    const sampleYBTData = {
      patientId: patientId,
      entryDate: new Date().toISOString(),
      biometricsData: {
        balance: {
          // Raw YBT measurements that will trigger calculated parameters
          ybt_right_composite_reach: 95.5,
          ybt_left_composite_reach: 88.2,
          ybt_composite_deviation: 7.8,
          ybt_anterior_deviation: 5.2,
          ybt_posteromedial_deviation: 8.1,
          ybt_posterolateral_deviation: 6.5
          // Performance ratio will be calculated automatically: (|95.5 - 88.2| / ((95.5 + 88.2) / 2)) * 100 = 7.95%
        }
      }
    };
    
    console.log('📊 Sample data to be added:', sampleYBTData);
    
    // Call the biometrics data API endpoint
    const response = await fetch(`/api/v1/patients/${patientId}/flowsheet/biometrics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(sampleYBTData)
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Sample YBT data added successfully!');
      console.log('📊 Result:', result);
      
      // Calculate expected performance ratio for verification
      const rightReach = 95.5;
      const leftReach = 88.2;
      const asymmetry = Math.abs(rightReach - leftReach);
      const average = (rightReach + leftReach) / 2;
      const expectedPerformanceRatio = (asymmetry / average) * 100;
      
      console.log('\n🧮 Expected Calculations:');
      console.log(`  Right Composite Reach: ${rightReach} cm`);
      console.log(`  Left Composite Reach: ${leftReach} cm`);
      console.log(`  Expected Performance Ratio: ${expectedPerformanceRatio.toFixed(2)}%`);
      console.log(`  Composite Deviation: 7.8%`);
      
      console.log('\n🔄 Please refresh the Vietnamese flowsheet to see:');
      console.log('  - YBT Tầm với tổng hợp - Phải: 95.5 cm');
      console.log('  - YBT Tầm với tổng hợp - Trái: 88.2 cm');
      console.log('  - YBT Tỷ lệ hiệu suất: ~7.95%');
      console.log('  - YBT Độ lệch tổng hợp: 7.8%');
      console.log('  - YBT Độ lệch trước: 5.2%');
      console.log('  - YBT Độ lệch sau trong: 8.1%');
      console.log('  - YBT Độ lệch sau ngoài: 6.5%');
      
      return { success: true, data: result, expectedPerformanceRatio: expectedPerformanceRatio.toFixed(2) };
    } else {
      const errorData = await response.json();
      console.error('❌ Failed to add sample YBT data:', errorData);
      return { success: false, error: errorData };
    }
    
  } catch (error) {
    console.error('❌ Error adding sample YBT data:', error);
    return { success: false, error: error.message };
  }
};

// Alternative method using direct flowsheet entry API
export const addSampleYBTDataDirect = async () => {
  try {
    console.log('🔧 Adding sample YBT data directly...');
    
    const currentUrl = window.location.href;
    const patientIdMatch = currentUrl.match(/patients\/([a-f0-9]{24})/);
    
    if (!patientIdMatch) {
      console.error('❌ Could not find patient ID in current URL');
      return { success: false, error: 'No patient ID found' };
    }
    
    const patientId = patientIdMatch[1];
    const entryDate = new Date().toISOString();
    
    // Direct flowsheet entries
    const ybtEntries = [
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_right_composite_reach',
        value: 95.5,
        units: 'cm'
      },
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_left_composite_reach',
        value: 88.2,
        units: 'cm'
      },
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_composite_deviation',
        value: 7.8,
        units: '%'
      },
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_anterior_deviation',
        value: 5.2,
        units: '%'
      },
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_posteromedial_deviation',
        value: 8.1,
        units: '%'
      },
      {
        patientId: patientId,
        entryDate: entryDate,
        parameterName: 'biometrics_balance_ybt_posterolateral_deviation',
        value: 6.5,
        units: '%'
      }
    ];
    
    console.log(`📊 Adding ${ybtEntries.length} YBT entries...`);
    
    // Add each entry
    const results = [];
    for (const entry of ybtEntries) {
      const response = await fetch(`/api/v1/patients/${patientId}/flowsheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(entry)
      });
      
      if (response.ok) {
        const result = await response.json();
        results.push(result);
        console.log(`✅ Added: ${entry.parameterName} = ${entry.value} ${entry.units}`);
      } else {
        const errorData = await response.json();
        console.error(`❌ Failed to add ${entry.parameterName}:`, errorData);
      }
    }
    
    console.log(`\n🎉 Added ${results.length} YBT parameters successfully!`);
    console.log('🔄 Refresh the Vietnamese flowsheet to see the new parameters');
    
    return { success: true, entriesAdded: results.length, patientId };
    
  } catch (error) {
    console.error('❌ Error adding sample YBT data:', error);
    return { success: false, error: error.message };
  }
};

// Make functions available globally for console use
if (typeof window !== 'undefined') {
  window.addSampleYBTData = addSampleYBTData;
  window.addSampleYBTDataDirect = addSampleYBTDataDirect;
}

// Instructions
console.log(`
🔧 YBT SAMPLE DATA UTILITY
=========================

To add sample YBT data for testing Vietnamese translations:

Method 1 (Recommended):
1. Open browser console (F12)
2. Run: addSampleYBTData()
3. Refresh the Vietnamese flowsheet

Method 2 (Direct):
1. Run: addSampleYBTDataDirect()
2. Refresh the Vietnamese flowsheet

This will add sample YBT data including:
- Composite Reach values (triggers Performance Ratio calculation)
- Deviation percentages
- All parameters with Vietnamese translations

Expected Vietnamese display:
- YBT Tầm với tổng hợp - Phải/Trái (Composite Reach)
- YBT Tỷ lệ hiệu suất (Performance Ratio)
- YBT Độ lệch tổng hợp (Composite Deviation)
- YBT Độ lệch trước/sau trong/sau ngoài (Individual Deviations)
`);
